import React from 'react';
import { Property, PropertyInput } from '../services/propertyAPI';
import { Location } from '../services/locationAPI';

interface PropertyListProps {
  properties: Property[];
  locations: Location[];
  loading: boolean;
  selectedPropertyIds: Set<string>;
  onPropertySelectionChange: (propertyId: string, isSelected: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onEditProperty: (property: Property) => void;
  onCopyProperty: (property: Property) => void;
  onDeleteProperty: (propertyId: string) => void;
  onUpdateProperty: (id: string, data: Partial<PropertyInput>) => Promise<void>;
  onCancelEdit: () => void;
  editingPropertyId: string | null;
  formData: PropertyInput;
  onFormDataChange: (field: keyof PropertyInput, value: any) => void;
}

const PropertyList: React.FC<PropertyListProps> = ({
  properties,
  locations,
  loading,
  selectedPropertyIds,
  onPropertySelectionChange,
  onSelectAll,
  onDeselectAll,
  onEditProperty,
  onCopyProperty,
  onDeleteProperty,
  onUpdateProperty,
  onCancelEdit,
  editingPropertyId,
  formData,
  onFormDataChange,
}) => {
  const renderInputField = (field: keyof PropertyInput, label: string, type: string = 'text') => (
    <div className="mb-3">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={String(formData[field] || '')}
        onChange={(e) => onFormDataChange(field, type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
      />
    </div>
  );



  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">Loading properties...</div>
      </div>
    );
  }

  if (properties.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="text-gray-600">No properties added yet. Click "Add Property" to get started.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {properties.map((property) => (
        <div key={property.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={selectedPropertyIds.has(property.id!)}
                onChange={(e) => onPropertySelectionChange(property.id!, e.target.checked)}
                className="w-4 h-4 text-vista-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-vista-teal-500 focus:ring-2"
              />
              <div>
                <h3 className="text-lg font-semibold text-gray-800">{property.name}</h3>
                <p className="text-gray-600">{property.address}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2 text-sm">
                  <div>
                    <span className="font-medium">Purchase Price:</span> €{property.purchase_price_eur.toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Purchase Date:</span> {property.purchase_date}
                  </div>
                  <div>
                    <span className="font-medium">Property Growth (nominal):</span> {property.annual_appreciation_pct?.toFixed(1) || 0}%
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {editingPropertyId === property.id ? (
                <>
                  <button
                    onClick={() => onUpdateProperty(property.id!, formData)}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={onCancelEdit}
                    className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                                     <button
                     onClick={() => onEditProperty(property)}
                     className="bg-vista-teal-600 text-white px-3 py-1 rounded text-sm hover:bg-vista-teal-700 transition-colors"
                   >
                     Edit
                   </button>
                  <button
                    onClick={() => onCopyProperty(property)}
                    className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => onDeleteProperty(property.id!)}
                    className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          

          {/* Inline Edit Form */}
          {editingPropertyId === property.id && (
            <div className="mt-6 pt-6 border-t border-gray-200 bg-yellow-100 p-4 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-700 mb-4">Edit Property: {property.name}</h4>
              
                             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                 {/* Basic Property Info */}
                 <div>
                   <h5 className="text-md font-semibold text-gray-700 mb-3">Basic Information</h5>
                   {renderInputField('name', 'Property Name')}
                   {renderInputField('address', 'Address')}
                   {renderInputField('purchase_date', 'Purchase Date', 'date')}
                   {renderInputField('rental_start_date', 'Rental Start Date', 'date')}
                   {renderInputField('purchase_price_eur', 'Purchase Price (€)', 'number')}
                   {renderInputField('annual_appreciation_pct', 'Property Growth (%/yr, nominal - price to purchase and sale)', 'number')}
                   
                   {/* Location Selection */}
                   <div className="mb-3">
                     <label className="block text-sm font-medium text-gray-700 mb-1">
                       Location *
                     </label>
                                           <select
                        value={formData.location_id || ''}
                        onChange={(e) => onFormDataChange('location_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500 bg-white text-gray-900"
                        style={{ backgroundColor: 'white' }}
                        required
                      >
                        <option value="" className="bg-white text-gray-900">Select a location...</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id} className="bg-white text-gray-900">
                            {location.name}
                          </option>
                        ))}
                      </select>
                   </div>
                 </div>

                 {/* Purchase Costs */}
                 <div>
                   <h5 className="text-md font-semibold text-gray-700 mb-3">Purchase Costs</h5>
                   {renderInputField('notaire_fees_pct', 'Notaire Fees (%)', 'number')}
                   {renderInputField('renovation_refurbishment_eur', 'Renovation & Refurbishment (€)', 'number')}
                   {renderInputField('furnishing_equipment_eur', 'Furnishing & Equipment (€)', 'number')}
                   {renderInputField('legal_administrative_eur', 'Legal & Administrative (€)', 'number')}
                   {renderInputField('property_transfer_tax_pct', 'Property Transfer Tax (%)', 'number')}
                 </div>

                 {/* Sale Details */}
                 <div>
                   <h5 className="text-md font-semibold text-gray-700 mb-3">Sale Details</h5>
                   {renderInputField('sale_date', 'Sale Date', 'date')}
                   {renderInputField('sale_price_eur', 'Sale Price (€)', 'number')}
                   {renderInputField('sale_notaire_fees_pct', 'Sale Notaire Fees (%)', 'number')}
                   {renderInputField('sale_legal_fees_eur', 'Sale Legal Fees (€)', 'number')}
                   {renderInputField('sale_agency_fees_pct', 'Sale Agency Fees (%)', 'number')}
                   {renderInputField('sale_other_fees_eur', 'Other Sale Fees (€)', 'number')}
                 </div>

                 {/* Mortgage Details */}
                 <div>
                   <h5 className="text-md font-semibold text-gray-700 mb-3">Mortgage Details</h5>
                   {renderInputField('mortgage_rate_pct', 'Mortgage Rate (%)', 'number')}
                   {renderInputField('mortgage_term_years', 'Mortgage Term (Years)', 'number')}
                   {renderInputField('deposit_eur', 'Deposit (€)', 'number')}
                 </div>

                 {/* Operating Costs */}
                 <div>
                   <h5 className="text-md font-semibold text-gray-700 mb-3">Operating Costs</h5>
                   {renderInputField('management_fee_pct', 'Management Fee (%)', 'number')}
                   {renderInputField('cleaning_per_booking_eur', 'Cleaning per Booking (€)', 'number')}
                   {renderInputField('average_stay_length_days', 'Average Stay Length (days)', 'number')}
                   {renderInputField('utilities_monthly_eur', 'Utilities Monthly (€)', 'number')}
                   {renderInputField('repairs_monthly_eur', 'Repairs Monthly (€)', 'number')}
                   {renderInputField('tourist_tax_per_guest_night_eur', 'Tourist Tax per Guest-Night (€)', 'number')}
                   {renderInputField('condo_fee_monthly_eur', 'Condo Fees Monthly (€)', 'number')}
                   {renderInputField('taxe_fonciere_annual_eur', 'Taxe Foncière Annual (€)', 'number')}
                   {renderInputField('insurance_annual_eur', 'Insurance Annual (€)', 'number')}
                   {renderInputField('accountant_france_annual_eur', 'French Accountant Annual (€)', 'number')}
                   {renderInputField('accountant_uk_annual_eur', 'UK Accountant Annual (€)', 'number')}
                   {renderInputField('capex_provision_annual_pct', 'Capex Provision Annual (%)', 'number')}
                 </div>
               </div>

               {/* Form Action Buttons */}
               <div className="flex gap-3 mt-6 pt-4 border-t border-gray-200">
                 <button
                   onClick={() => onUpdateProperty(property.id!, formData)}
                   className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                 >
                   Update Property
                 </button>
                 <button
                   onClick={onCancelEdit}
                   className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
                 >
                   Cancel
                 </button>
               </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default PropertyList;
